if (!customElements.get('bundle-builder')) {
  const BUNDLE_CODE_PREFIX = 'GSB-';
  const rootUrl = () => window.Shopify?.routes?.root || '/';

  const unique = (values) => Array.from(new Set(values.filter(Boolean)));
  const sameCodes = (left, right) => {
    const normalize = (values) => unique(values.map((value) => String(value).toLowerCase())).sort();
    return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right));
  };

  const signatureFor = (quantitiesByVariant) =>
    Array.from(quantitiesByVariant, ([id, quantity]) => [Number(id), Number(quantity)])
      .sort((left, right) => left[0] - right[0])
      .map(([id, quantity]) => `${id}:${quantity}`)
      .join('|');

  async function fetchCart() {
    const response = await fetch(`${rootUrl()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('The basket could not be checked. Please try again.');
    return response.json();
  }

  function discountPlan(cart) {
    const groups = new Map();
    const knownBundleCodes = new Set();

    (cart.items || []).forEach((item) => {
      const properties = item.properties || {};
      const code = String(properties._gs_bundle_discount_code || '').trim();
      const instance = String(properties._gs_bundle_instance || '').trim();
      const expectedSignature = String(properties._gs_bundle_signature || '').trim();
      if (code) knownBundleCodes.add(code.toLowerCase());
      if (!instance || !expectedSignature) return;

      if (!groups.has(instance)) {
        groups.set(instance, {
          code,
          expectedSignature,
          metadataValid: true,
          quantitiesByVariant: new Map(),
        });
      }
      const group = groups.get(instance);
      if (group.code !== code || group.expectedSignature !== expectedSignature) group.metadataValid = false;
      const variantId = Number(item.variant_id);
      group.quantitiesByVariant.set(
        variantId,
        (group.quantitiesByVariant.get(variantId) || 0) + Number(item.quantity || 0)
      );
    });

    const validBundleCodes = [];
    groups.forEach((group) => {
      if (
        group.code &&
        group.metadataValid &&
        signatureFor(group.quantitiesByVariant) === group.expectedSignature
      ) {
        validBundleCodes.push(group.code);
      }
    });

    const currentCodes = (cart.discount_codes || [])
      .map((discount) => String(discount?.code || '').trim())
      .filter(Boolean);
    currentCodes.forEach((code) => {
      if (code.toUpperCase().startsWith(BUNDLE_CODE_PREFIX)) knownBundleCodes.add(code.toLowerCase());
    });
    const nonBundleCodes = currentCodes.filter((code) => !knownBundleCodes.has(code.toLowerCase()));
    const desiredCodes = unique([...nonBundleCodes, ...validBundleCodes]);
    if (desiredCodes.length > 5) {
      throw new Error('This basket has too many discount codes. Please remove one before adding another bundle.');
    }
    return { currentCodes, desiredCodes, validBundleCodes: unique(validBundleCodes) };
  }

  async function writeDiscountCodes(codes) {
    const response = await fetch(`${rootUrl()}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    const cart = await response.json();
    if (!response.ok || cart.status) {
      throw new Error(cart.description || cart.message || 'The bundle discount could not be applied.');
    }
    return cart;
  }

  let reconciliationPromise;
  async function reconcileBundleDiscounts({ requiredCode = '' } = {}) {
    if (reconciliationPromise) return reconciliationPromise;
    reconciliationPromise = (async () => {
      const before = await fetchCart();
      const plan = discountPlan(before);
      const changed = !sameCodes(plan.currentCodes, plan.desiredCodes);
      const after = changed ? await writeDiscountCodes(plan.desiredCodes) : before;
      const appliedCodes = new Set(
        (after.discount_codes || [])
          .filter((discount) => discount && discount.applicable !== false)
          .map((discount) => String(discount.code || '').toLowerCase())
      );
      const missingBundleCode = plan.validBundleCodes.find(
        (code) => !appliedCodes.has(code.toLowerCase())
      );
      if (missingBundleCode || (requiredCode && !appliedCodes.has(requiredCode.toLowerCase()))) {
        throw new Error(
          'The bundle discount is not available yet. The bundle has not been left in your basket.'
        );
      }
      return { changed, cart: after };
    })();
    try {
      return await reconciliationPromise;
    } finally {
      reconciliationPromise = undefined;
    }
  }

  async function removeBundleInstance(instanceId) {
    const cart = await fetchCart();
    const updates = {};
    (cart.items || []).forEach((item) => {
      if (item.properties?._gs_bundle_instance === instanceId) updates[item.key] = 0;
    });
    if (!Object.keys(updates).length) return;
    const response = await fetch(`${rootUrl()}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) throw new Error('The incomplete bundle could not be removed from the basket.');
  }

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.addButton = this.querySelector('[data-bundle-add]');
      this.status = this.querySelector('.bundle-builder__status');
      if (!this.addButton || this.addButton.dataset.bound === 'true') return;

      this.addButton.dataset.bound = 'true';
      this.addButton.addEventListener('click', () => this.addBundle());
    }

    selectedItems() {
      const quantitiesByVariant = new Map();
      const stockByVariant = new Map();

      this.querySelectorAll('[data-bundle-variant]').forEach((control) => {
        const variantId = Number.parseInt(control.value, 10);
        const quantity = Number.parseInt(control.dataset.bundleQuantity || '1', 10);
        if (!Number.isInteger(variantId) || variantId <= 0 || !Number.isInteger(quantity) || quantity <= 0) return;
        quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + quantity);

        const source = control.selectedOptions?.[0] || control;
        const managed = source.dataset.inventoryManaged === 'true';
        const policy = source.dataset.inventoryPolicy || 'deny';
        const available = Number.parseInt(source.dataset.inventoryQuantity || '0', 10);
        if (managed && policy !== 'continue' && Number.isInteger(available)) {
          stockByVariant.set(variantId, available);
        }
      });

      const bundleId = this.dataset.bundleId || '';
      const bundleTitle = this.dataset.bundleTitle || 'Bundle offer';
      const discountCode = this.dataset.discountCode || '';
      const signature = signatureFor(quantitiesByVariant);
      const instanceId = `${bundleId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

      quantitiesByVariant.forEach((quantity, variantId) => {
        const available = stockByVariant.get(variantId);
        if (Number.isInteger(available) && quantity > available) {
          throw new Error(`Only ${available} of one selected option is currently available.`);
        }
      });

      const items = Array.from(quantitiesByVariant, ([id, quantity]) => ({
        id,
        quantity,
        properties: {
          Bundle: bundleTitle,
          _gs_bundle_config: bundleId,
          _gs_bundle_instance: instanceId,
          _gs_bundle_signature: signature,
          _gs_bundle_title: bundleTitle,
          _gs_bundle_discount_code: discountCode,
        },
      }));
      return { items, instanceId, discountCode, bundleId };
    }

    setLoading(loading) {
      this.addButton.disabled = loading;
      this.addButton.classList.toggle('loading', loading);
      this.addButton.setAttribute('aria-disabled', loading ? 'true' : 'false');
      const spinner = this.addButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !loading);
    }

    announce(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('bundle-builder__status--error', isError);
    }

    async addBundle() {
      if (this.addButton.disabled || this.addButton.classList.contains('loading')) return;

      let selection;
      try {
        selection = this.selectedItems();
      } catch (error) {
        this.announce(error.message || 'Please review the selected bundle options.', true);
        return;
      }
      if (!selection.items.length) {
        this.announce('Please choose the bundle items first.', true);
        return;
      }

      this.setLoading(true);
      this.announce('Adding bundle to basket…');

      try {
        if (selection.discountCode) {
          const currentCart = await fetchCart();
          const duplicateOffer = (currentCart.items || []).some(
            (item) => String(item.properties?._gs_bundle_config || '') === selection.bundleId
          );
          if (duplicateOffer) {
            throw new Error(
              'This bundle offer is already in your basket. Remove it before adding the same offer again.'
            );
          }
        }

        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: selection.items }),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'The bundle could not be added.');
        }

        this.announce('Bundle added. Checking its discount…');
        try {
          await reconcileBundleDiscounts({ requiredCode: selection.discountCode });
        } catch (discountError) {
          await removeBundleInstance(selection.instanceId);
          await reconcileBundleDiscounts();
          throw discountError;
        }
        window.location.assign(window.routes.cart_url || '/cart');
      } catch (error) {
        this.announce(error.message || 'The bundle could not be added. Please try again.', true);
        this.setLoading(false);
      }
    }
  }

  customElements.define('bundle-builder', BundleBuilder);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (event.source !== 'cart-items') return;
      try {
        const result = await reconcileBundleDiscounts();
        if (result.changed && window.location.pathname.endsWith('/cart')) window.location.reload();
      } catch (error) {
        console.error('Bundle discount reconciliation failed:', error);
      }
    });
  }
}
